/**
 * Nút đăng nhập Google và Apple trong dialog auth.
 *
 * Mount Google Sign-In button động; Apple dùng popup SDK.
 * Trả về null nếu không bật provider nào trong env.
 */
import { useEffect, useRef, useState } from 'react';
import { TEXT } from '../../shared/languages';
import {
  isAppleAuthEnabled,
  isGoogleAuthEnabled,
  mountGoogleSignInButton,
  signInWithApple,
} from '../../shared/utils/socialAuth';

const AppleIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M16.365 1.43c0 1.14-.46 2.27-1.28 3.1-.9.9-2.12 1.41-3.28 1.33-.08-1.1.42-2.26 1.22-3.08.88-.92 2.35-1.46 3.34-1.35zM20.88 17.17c-.57 1.3-.85 1.88-1.58 3.03-1.02 1.58-2.46 3.55-4.25 3.56-1.59.01-2-.98-4.05-.97-2.05.01-2.48 1-4.07.98-1.79-.01-3.15-1.75-4.17-3.33-2.87-4.42-3.17-9.6-1.4-12.36 1.25-1.98 3.22-3.14 5.07-3.14 1.88 0 3.06 1.01 4.62 1.01 1.49 0 2.39-.99 4.52-1.01 1.61-.02 3.32.88 4.57 2.4-4.02 2.19-3.36 7.88.67 9.43-.83 1.07-1.74 2.13-2.44 2.13-.74 0-1.05-.68-2.12-.68z" />
  </svg>
);

/**
 * Nút xác thực mạng xã hội.
 * @param {Object} props
 * @param {Function} props.onGoogleSuccess
 * @param {Function} props.onAppleSuccess
 * @param {Function} [props.onError]
 * @param {boolean} [props.disabled]
 * @param {boolean} [props.active]
 */
export const SocialAuthButtons = ({
  onGoogleSuccess,
  onAppleSuccess,
  onError,
  disabled = false,
  active = true,
}) => {
  const [isApplePending, setIsApplePending] = useState(false);
  const googleButtonRef = useRef(null);
  const googleSuccessRef = useRef(onGoogleSuccess);
  const googleErrorRef = useRef(onError);
  const t = TEXT.auth;
  const googleEnabled = isGoogleAuthEnabled();
  const appleEnabled = isAppleAuthEnabled();

  googleSuccessRef.current = onGoogleSuccess;
  googleErrorRef.current = onError;

  useEffect(() => {
    const googleButton = googleButtonRef.current;

    if (!googleEnabled || !active || !googleButton) {
      return undefined;
    }

    let cancelled = false;

    mountGoogleSignInButton(googleButton, {
      onSuccess: (credential) => googleSuccessRef.current?.(credential),
      onError: () => googleErrorRef.current?.(t.socialLoginFailed),
    }).catch(() => {
      if (!cancelled) {
        googleErrorRef.current?.(t.socialLoginFailed);
      }
    });

    return () => {
      cancelled = true;

      if (googleButton) {
        googleButton.innerHTML = '';
      }
    };
  }, [active, googleEnabled, t.socialLoginFailed]);

  if (!googleEnabled && !appleEnabled) {
    return null;
  }

  const handleAppleClick = async () => {
    if (disabled || isApplePending) {
      return;
    }

    setIsApplePending(true);

    try {
      const result = await signInWithApple();
      await onAppleSuccess?.(result);
    } catch (error) {
      const message = error?.error || error?.message;

      if (message && !String(message).includes('popup_closed')) {
        onError?.(t.socialLoginFailed);
      }
    } finally {
      setIsApplePending(false);
    }
  };

  return (
    <div className="auth-dialog-social-row">
      {googleEnabled && (
        <div
          ref={googleButtonRef}
          className={`auth-dialog-social-google ${disabled ? 'is-disabled' : ''}`}
        />
      )}

      {appleEnabled && (
        <button
          type="button"
          className="auth-dialog-social-btn auth-dialog-social-btn--apple"
          onClick={handleAppleClick}
          disabled={disabled || isApplePending}
          aria-label={t.continueWithApple}
        >
          <AppleIcon />
        </button>
      )}
    </div>
  );
};
